import { FC, useEffect, useState } from 'react'
import {
  MainPage,
  PageTitle,
  CoreStats,
  StatsGlassGrid,
  InfoStatsGrid,
  InfoStatItem,
} from '../components'
import LoadingIcon from '../components/LoadingIcon'
import logo from '../assets/images/TealcaLogo.png'
import { countSearchShipmentPendingAccount } from '../services'
import { useAppSelector } from '../store/hooks'
import {
  TruckIcon,
  DocumentTextIcon,
  XCircleIcon,
  ClipboardDocumentCheckIcon,
  InboxArrowDownIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'

const Dashboard: FC = () => {
  const user = useAppSelector((state) => state.user)

  const demoGroups: CoreStats[] = [
    {
      title: 'Guías generadas',
      today: 0,
      last7: 0,
      last30: 0,
      color: 'bg-blue-500',
      consultOrigin: 'original',
    },
    {
      title: 'Guías entregadas',
      today: 0,
      last7: 0,
      last30: 0,
      color: 'bg-emerald-500',
      consultOrigin: 'main',
    },
  ]

  const demoInfoGroups: InfoStatItem[] = [
    {
      title: 'Guías en tránsito',
      value: 0,
      icon: TruckIcon,
    },
    {
      title: 'Guías por facturar',
      value: 0,
      icon: DocumentTextIcon,
    },
  ]

  const [groups, setGroups] = useState<CoreStats[]>(demoGroups)
  const [infoGroups, setInfoGroups] = useState<InfoStatItem[]>(demoInfoGroups)
  const [isLoading, setIsLoading] = useState(false)
  const [hasData, setHasData] = useState(false)

  const renderTitle = (title: string) => {
    return title.split('\n').map((line, i, arr) => (
      <span key={i}>
        {line}
        {i < arr.length - 1 && <br />}
      </span>
    ))
  }

  useEffect(() => {
    const accountId = user?.client?.id
    const accountCode = user?.client?.accountCode
    if (!accountId && !accountCode) return

    setIsLoading(true)

    const load = async () => {
      const response = await countSearchShipmentPendingAccount(
        accountCode,
        accountCode == null ? accountId : undefined
      ).finally(() => {
        setIsLoading(false)
      })

      if (!response?.didError && response?.model) {
        const m = response.model
        const newGroups: CoreStats[] = [
          {
            title: 'Guías generadas',
            today: m.createdToday,
            last7: m.createdLast7Days,
            last30: m.createdLast30Days,
            currentYear: m.createdCurrentYear,
            color: 'bg-blue-500',
            consultOrigin: 'original',
            icon: InboxArrowDownIcon,
          },
          {
            title: 'Guías entregadas',
            today: m.deliveredToday,
            last7: m.deliveredLast7Days,
            last30: m.deliveredLast30Days,
            currentYear: m.deliveredCurrentYear,
            color: 'bg-emerald-500',
            consultOrigin: 'main',
            icon: CheckCircleIcon,
          },
          {
            title: 'Guías anuladas',
            today: m.voidedToday,
            last7: m.voidedLast7Days,
            last30: m.voidedLast30Days,
            currentYear: m.voidedCurrentYear,
            color: 'bg-emerald-500',
            consultOrigin: 'info',
            icon: XCircleIcon,
          },
          {
            title: 'Guías facturadas',
            today: m.pendingDocsToday,
            last7: m.pendingDocsLast7Days,
            last30: m.pendingDocsLast30Days,
            currentYear: m.pendingDocsCurrentYear,
            color: 'bg-emerald-500',
            consultOrigin: 'info',
            icon: ClipboardDocumentCheckIcon,
          },
        ]
        setGroups(newGroups)

        const newInfoGroups: InfoStatItem[] = [
          {
            title: 'Guías en tránsito',
            value: m.totalInTransit ?? 0,
            icon: TruckIcon,
          },
          {
            title: 'Guías por facturar',
            value: m.totalToInvoice ?? 0,
            icon: DocumentTextIcon,
          },
        ]
        setInfoGroups(newInfoGroups)

        setHasData(true)
      }
    }

    load()
  }, [user?.client?.id, user?.client?.accountCode])
  return (
    <MainPage>
      <PageTitle title="Panel de inicio" />

      {/* Diseños de ejemplo para KPIs de guías */}
      {isLoading && !hasData ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center">
            <div className="relative">
              <LoadingIcon size="8rem" />
              <img
                src={logo}
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-15 h-16 animate-pulse"
                alt="Tealca logo"
              />
            </div>
            <p className="text-center font-bold break-words px-2 mt-4 animate-pulse whitespace-pre-line">
              {renderTitle('Cargando detalles...\nPor favor espere')}
            </p>
          </div>
        </div>
      ) : (
        <div className="mt-4 space-y-9">
          <StatsGlassGrid groups={groups} />
          <div className="w-full xl:w-[calc(50%-1.125rem)]">
            <InfoStatsGrid
              items={infoGroups}
              iconStyle="solid"
              layout="spread"
            />
          </div>
          {/* <StatsAuroraGrid groups={groups} /> */}
          {/* <StatsSplitPanel groups={groups} /> */}
          {/* <StatsSplitPanelIcons groups={groups} /> */}
          {/* <StatsCardsGrid groups={groups} /> */}
          {/* <StatsCompactRow groups={groups} /> */}
          {/* <StatsYearCards groups={groups} /> */}
        </div>
      )}

      {/* Para periodo dinámico y gráfico, ver <LastPeriodStats /> */}
    </MainPage>
  )
}

export default Dashboard
